import React, { useCallback, useEffect, useState } from "react";
import {useTranslation} from "react-i18next";

// Libs
import { blueColor, primaryColor } from "../common/libs/theme-lib";

// Components
import { Classes, Dialog, Icon } from "@blueprintjs/core";
import Button from "../common/components/button";
import PasswordInput from "../common/components/password-input";

// Actions
import { updateUserPassword } from "./users-action";
import { useDispatch } from "react-redux";

// Redux dispatch hook
const useReduxDispatch = () => {
    const dispatch = useDispatch();
    return {
        updateUserPassword: (user) => dispatch(updateUserPassword(user))
    }
};

const UpdatePasswordUserDialog = (props) => {
    const {isOpen, onClose, user} = props;

    // Redux
    const { updateUserPassword } = useReduxDispatch();

    // i18n
    const {t} = useTranslation("users");

    const [password, setPassword] = useState({value: "", isValid: false});
    const [verifyPassword, setVerifyPassword] = useState({value: "", isValid: false});

    const [validInputs, setValidInputs] = useState({
        validLength: false,
        validLower: false,
        validUpper: false,
        validNumber: false,
        validSpecial: false
    });
    const [validForm, setValidForm] = useState(false);
    const [validValuePassword, setValidValuePassword] = useState(false);

    // password validation
    function validatePassword(password) {
        const validLength = password.length >= 6;
        const validLower = password.search(/[a-z]/) !== -1;
        const validUpper = password.search(/[A-Z]/) !== -1;
        const validNumber = password.search(/[0-9]/) !== -1;
        const validSpecial = password.search(/[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/) !== -1;

        setValidInputs({validLength: validLength, validLower: validLower, validUpper: validUpper, validNumber: validNumber, validSpecial: validSpecial});

        const isValid = validLength && validUpper && validLower && validNumber && validSpecial;

        setPassword({value: password, isValid: isValid});
    }

    function valueVerifyPassword(value) {
        setVerifyPassword({value: value});
    }

    function validateValuePassword() {
        password.value === verifyPassword.value ? setValidValuePassword(true) : setValidValuePassword(false);
    }

    function validateForm() {
        password.isValid && (password.value === verifyPassword.value) ?
            setValidForm(true) : setValidForm(false);
    }

    const validatePasswords = useCallback(() => {
        validateForm();
        validateValuePassword();
    }, [password, verifyPassword]);
    
    useEffect(() => {
        validatePasswords();
    }, [validatePasswords]);
    
    return (
        <Dialog
            isOpen={isOpen}
            onClose={onClose}
            lazy={true}
            style={{
                backgroundColor: "white",
                paddingBottom: "40px"
            }}
        >
            <div
                style={{ margin: "20px 40px" }}
                className={Classes.DIALOG_BODY}
            >
                <h3 style={{ margin: "20px 0" }}>{t("popover.changePassword")}</h3>
                <h4 style={{ color: blueColor }}>{user.full_name}</h4>
                <div style={{
                    backgroundColor: "white",
                    padding: 15,
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    width: "100%"
                }}>
                    <div style={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%"
                    }}>
                        <PasswordInput
                            label={t("updatePasswordUserDialog.inputs.newPassword")}
                            style={loginItemStyle}
                            value={password.value}
                            setValue={validatePassword}
                        />
                        {!password.isValid && password.value.length >= 1 && (
                            <span style={errorStyle}>{t("updatePasswordUserDialog.validations.password")}</span>
                        )}
                        <PasswordInput
                            label={t("updatePasswordUserDialog.inputs.verifyNewPassword")}
                            style={loginItemStyle}
                            value={verifyPassword.value}
                            setValue={valueVerifyPassword}
                        />
                        {!validValuePassword && verifyPassword.value.length >= 1 && (
                            <span style={errorStyle}>{t("updatePasswordUserDialog.validations.verifyPassword")}</span>
                        )}
                    </div>
                    <div style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        justifyContent: "space-between"
                    }}>
                        <div style={{
                            display: "flex",
                            flexDirection: "column",
                            marginTop: "15px"
                        }}>
                            <div style={{
                                display: "flex"
                            }}>
                                <div style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    width: 20,
                                    height: 20,
                                    border: "1px solid black",
                                    borderRadius: 25,
                                    marginRight: 10
                                }}>
                                    <Icon icon="high-priority" iconSize={12} style={{margin: "auto"}}/>
                                </div>
                                <span>{t("updatePasswordUserDialog.requirements.title")}</span>
                            </div>
                            <ul>
                                <li style={liStyle}>
                                    <div style={{
                                        display: "flex",
                                        justifyContent: "space-between"
                                    }}>
                                        <div>
                                            {t("updatePasswordUserDialog.requirements.characters")}
                                        </div>
                                        <div>
                                            {validInputs.validLength && (
                                                <Icon icon="tick" iconSize={16} />
                                            )}
                                        </div>
                                    </div>
                                </li>
                                <li style={liStyle}>
                                    <div style={{
                                        display: "flex",
                                        justifyContent: "space-between"
                                    }}>
                                        <div>
                                            {t("updatePasswordUserDialog.requirements.lower")}
                                        </div>
                                        <div>
                                            {validInputs.validLower && (
                                                <Icon icon="tick" iconSize={16} />
                                            )}
                                        </div>
                                    </div>
                                </li>
                                <li style={liStyle}>
                                    <div style={{
                                        display: "flex",
                                        justifyContent: "space-between"
                                    }}>
                                        <div>
                                            {t("updatePasswordUserDialog.requirements.upper")}
                                        </div>
                                        <div>
                                            {validInputs.validUpper && (
                                                <Icon icon="tick" iconSize={16} />
                                            )}
                                        </div>
                                    </div>
                                </li>
                                <li style={liStyle}>
                                    <div style={{
                                        display: "flex",
                                        justifyContent: "space-between"
                                    }}>
                                        <div>
                                            {t("updatePasswordUserDialog.requirements.number")}
                                        </div>
                                        <div>
                                            {validInputs.validNumber && (
                                                <Icon icon="tick" iconSize={16} />
                                            )}
                                        </div>
                                    </div>
                                </li>
                                <li style={liStyle}>
                                    <div style={{
                                        display: "flex",
                                        justifyContent: "space-between"
                                    }}>
                                        <div>
                                            {t("updatePasswordUserDialog.requirements.special")}
                                        </div>
                                        <div>
                                            {validInputs.validSpecial && (
                                                <Icon icon="tick" iconSize={16} />
                                            )}
                                        </div>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            <div
                style={{ margin: "20px 40px 0 40px" }}
                className={Classes.DIALOG_FOOTER}
            >
                <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                    <Button
                        text={t("editUserDialog.input.cancel")}
                        action={onClose}
                        intent={"cancel"}
                        style={{
                            marginRight: "15px",
                        }}
                    />
                    <Button
                        text={t("editUserDialog.input.save")}
                        action={() => {
                            updateUserPassword({
                                _id: user._id,
                                newPassword: password.value,
                                verifyPassword: verifyPassword.value,
                                ignoreCurrentPassword: true
                            });
                            onClose();
                        }}
                        intent={"secondary"}
                        disabled={validForm}
                    />
                </div>
            </div>
        </Dialog>
    )
}

export default UpdatePasswordUserDialog;

const loginItemStyle = {
    margin: "15px 0 0 0",
    display: "flex",
    flexDirection: "column",
    width: "100%"
}

const liStyle = {
    marginTop: "5px",
    width: "100%"
}

const errorStyle = {
    color: primaryColor,
    fontSize: 12
}
